import { toast } from 'sonner'
import { Switch } from '@/components/ui/switch'
import { EntityDetailSkeleton } from '@/components/layout/entity-detail-skeleton'
import { getSettingsSection } from '@/constants/settings-nav'
import {
  SettingsMatrixCell,
  SettingsMatrixHeader,
  SettingsMatrixRow,
} from '@/components/settings/settings-matrix'
import { SettingsPageShell } from '@/components/settings/settings-page-shell'
import { SettingsPanel } from '@/components/settings/settings-panel'
import { SettingsPanelEmpty } from '@/components/settings/settings-panel-empty'
import { SettingsSectionHeading } from '@/components/settings/settings-section-heading'
import {
  useNotificationPreferences,
  type NotificationPreferenceItem,
} from '@/hooks/use-notifications'

type ChannelKey = 'in_app_enabled' | 'email_enabled'

const AUDIENCE_ORDER = ['product', 'platform', 'candidate', 'employer']

const AUDIENCE_HEADINGS: Record<string, string> = {
  product: 'Product feedback',
  platform: 'Platform',
  candidate: 'As a candidate',
  employer: 'As an employer',
}

const NOTIFICATION_COLUMNS = [
  { key: 'in_app', label: 'In-app', className: 'w-12' },
  { key: 'email', label: 'Email', className: 'w-12' },
] as const

function audienceHeading(audience: string): string {
  if (AUDIENCE_HEADINGS[audience]) return AUDIENCE_HEADINGS[audience]
  const label = audience.replace(/_/g, ' ')
  return `As a ${label}`
}

function groupByAudience(
  items: NotificationPreferenceItem[],
): Array<{ audience: string; items: NotificationPreferenceItem[] }> {
  const groups = new Map<string, NotificationPreferenceItem[]>()
  for (const item of items) {
    const existing = groups.get(item.audience)
    if (existing) {
      existing.push(item)
    } else {
      groups.set(item.audience, [item])
    }
  }
  return [...groups.entries()]
    .sort(([a], [b]) => {
      const ai = AUDIENCE_ORDER.indexOf(a)
      const bi = AUDIENCE_ORDER.indexOf(b)
      return (ai === -1 ? AUDIENCE_ORDER.length : ai) - (bi === -1 ? AUDIENCE_ORDER.length : bi)
    })
    .map(([audience, groupItems]) => ({ audience, items: groupItems }))
}

export function SettingsNotificationsPage() {
  const section = getSettingsSection('notifications')
  const { preferences, isLoading, updatePreferences, isUpdating } =
    useNotificationPreferences()

  const handleToggle = async (
    item: NotificationPreferenceItem,
    channel: ChannelKey,
    enabled: boolean,
  ) => {
    try {
      await updatePreferences([
        {
          category: item.category,
          email_enabled: channel === 'email_enabled' ? enabled : item.email_enabled,
          in_app_enabled: channel === 'in_app_enabled' ? enabled : item.in_app_enabled,
        },
      ])
    } catch (error) {
      const message =
        error instanceof Error ? error.message : 'Failed to update notification settings'
      toast.error(message)
    }
  }

  if (isLoading && !preferences) {
    return (
      <SettingsPageShell
        title={section?.label ?? 'Notifications'}
        description={section?.description}
      >
        <EntityDetailSkeleton
          variant="settings"
          withPageContainer={false}
          ariaLabel="Loading notification settings"
        />
      </SettingsPageShell>
    )
  }

  const items = preferences ?? []
  const groups = groupByAudience(items)
  const showAudienceHeadings = groups.length > 1

  return (
    <SettingsPageShell
      title={section?.label ?? 'Notifications'}
      description={section?.description}
    >
      <SettingsPanel>
        <SettingsMatrixHeader leadingLabel="Notify me about" columns={[...NOTIFICATION_COLUMNS]} />
        {items.length === 0 ? (
          <SettingsPanelEmpty>No notification categories are available yet.</SettingsPanelEmpty>
        ) : (
          groups.map((group, groupIndex) => (
            <div key={group.audience}>
              {showAudienceHeadings ? (
                <SettingsSectionHeading isFirst={groupIndex === 0}>
                  {audienceHeading(group.audience)}
                </SettingsSectionHeading>
              ) : null}
              {group.items.map((item, index) => {
                const isLast =
                  groupIndex === groups.length - 1 && index === group.items.length - 1
                return (
                  <SettingsMatrixRow
                    key={item.category}
                    label={item.label}
                    description={item.description}
                    showDivider={!isLast}
                  >
                    <SettingsMatrixCell>
                      <Switch
                        checked={item.in_app_enabled}
                        disabled={isUpdating}
                        onCheckedChange={(checked) =>
                          handleToggle(item, 'in_app_enabled', checked)
                        }
                        aria-label={`In-app notifications for ${item.label}`}
                      />
                    </SettingsMatrixCell>
                    <SettingsMatrixCell>
                      <Switch
                        checked={item.email_enabled}
                        disabled={isUpdating}
                        onCheckedChange={(checked) =>
                          handleToggle(item, 'email_enabled', checked)
                        }
                        aria-label={`Email notifications for ${item.label}`}
                      />
                    </SettingsMatrixCell>
                  </SettingsMatrixRow>
                )
              })}
            </div>
          ))
        )}
      </SettingsPanel>
    </SettingsPageShell>
  )
}
